import axios from "axios";
import { CLIENT_HEADER, clientKind, isExtension } from "./client";
import { refreshStorage } from "./refresh-storage";

export type SessionPayload = {
  access_token: string;
  token_type?: string;
  expires_in?: number;
  session_id?: string;
  refresh_token?: string;
  new_user?: boolean;
};

export type AuthStatus = "unknown" | "signed-in" | "signed-out";

export type AuthState = {
  status: AuthStatus;
  // Stable for the life of a session, so it can sit in query keys without
  // changing every time the access token is refreshed.
  sessionKey: string | null;
};

// Token from builds before refresh tokens; upgraded or dropped on boot.
export const LEGACY_TOKEN_KEY = "unideck.auth.accessToken";
// Not a credential: the session id, so the next page load knows a refresh is
// worth trying and which cached data belongs to it while offline.
const SESSION_HINT_KEY = "unideck.auth.session";
const REFRESH_MARGIN_MS = 60_000;
const RETRY_DELAY_MS = 30_000;
const REFRESH_LOCK = "unideck-auth-refresh";
// Every tab waits on the refresh lock, so a hung request must not hold it.
const REFRESH_TIMEOUT_MS = 15_000;

export const authClient = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  withCredentials: true,
  headers: { [CLIENT_HEADER]: clientKind },
});

export const decodeClaims = (token: string): { sid?: string; exp?: number } => {
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return {};
  }
};

const statusOf = (error: unknown) =>
  axios.isAxiosError(error) ? error.response?.status : undefined;

type RefreshOutcome = "ok" | "invalid" | "transient";

export class TokenManager {
  private accessToken: string | null = null;
  private expiresAt = 0;
  private sessionKey: string | null = null;
  private status: AuthStatus = "unknown";
  private refreshing: Promise<RefreshOutcome> | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<(state: AuthState) => void>();
  private channel: BroadcastChannel | null = null;
  private notice: string | null = null;
  private resolveReady: () => void = () => {};
  readonly ready: Promise<void>;

  constructor() {
    this.ready = new Promise(resolve => {
      this.resolveReady = resolve;
    });

    if (typeof BroadcastChannel !== "undefined") {
      this.channel = new BroadcastChannel("unideck-auth");
      this.channel.addEventListener("message", event =>
        this.onBroadcast(event.data),
      );
    }

    if (typeof document !== "undefined") {
      const wake = () => {
        if (this.status === "signed-in" && this.isExpiring()) {
          void this.refresh();
        }
      };
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          wake();
        }
      });
      window.addEventListener("online", wake);
    }
  }

  getState(): AuthState {
    return { status: this.status, sessionKey: this.sessionKey };
  }

  subscribe(listener: (state: AuthState) => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getAccessToken() {
    return this.accessToken;
  }

  isSignedIn() {
    return this.status === "signed-in";
  }

  consumeNotice() {
    const notice = this.notice;
    this.notice = null;
    return notice;
  }

  private isExpiring(marginMs = REFRESH_MARGIN_MS) {
    return Date.now() >= this.expiresAt - marginMs;
  }

  async getValidAccessToken(): Promise<string | null> {
    if (this.status !== "signed-in") {
      return null;
    }
    if (!this.accessToken || this.isExpiring(30_000)) {
      await this.refresh();
    }
    // After a failed renewal an expired token would only earn a 401.
    return this.accessToken && !this.isExpiring(0) ? this.accessToken : null;
  }

  async applySession(payload: SessionPayload, broadcast = true) {
    const claims = decodeClaims(payload.access_token);
    this.accessToken = payload.access_token;
    this.expiresAt = payload.expires_in
      ? Date.now() + payload.expires_in * 1000
      : claims.exp
        ? claims.exp * 1000
        : Date.now() + 15 * 60_000;
    this.sessionKey =
      payload.session_id ?? claims.sid ?? payload.access_token.slice(-24);
    this.status = "signed-in";

    if (payload.refresh_token && isExtension) {
      await refreshStorage.set(payload.refresh_token);
    }
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.setItem(SESSION_HINT_KEY, this.sessionKey);

    this.schedule(this.expiresAt - Date.now() - REFRESH_MARGIN_MS);
    this.emit();
    if (broadcast) {
      this.channel?.postMessage({
        type: "signed-in",
        sessionKey: this.sessionKey,
      });
    }
  }

  refresh(): Promise<boolean> {
    return this.refreshOutcome().then(outcome => outcome === "ok");
  }

  private refreshOutcome(): Promise<RefreshOutcome> {
    if (!this.refreshing) {
      this.refreshing = this.runRefresh().finally(() => {
        this.refreshing = null;
      });
    }
    return this.refreshing;
  }

  // Tabs share one cookie or one stored token, so they take turns: no tab
  // sends a token that another tab is in the middle of replacing.
  private runRefresh(): Promise<RefreshOutcome> {
    const locks =
      typeof navigator !== "undefined" ? navigator.locks : undefined;
    return locks
      ? locks.request(REFRESH_LOCK, () => this.refreshOnce())
      : this.refreshOnce();
  }

  private async refreshOnce(retried = false): Promise<RefreshOutcome> {
    let sent: string | null = null;
    if (isExtension) {
      sent = await refreshStorage.get();
      if (!sent) {
        await this.clear(false);
        return "invalid";
      }
    }

    try {
      const response = await authClient.post(
        "/auth/session/refresh",
        sent ? { refresh_token: sent } : {},
        { timeout: REFRESH_TIMEOUT_MS },
      );
      await this.applySession(response.data.data, false);
      return "ok";
    } catch (error) {
      const status = statusOf(error);
      if (status === 401 || status === 400) {
        // A sign-in in another tab can store a new token meanwhile.
        if (isExtension && !retried && (await refreshStorage.get()) !== sent) {
          return this.refreshOnce(true);
        }
        await this.clear(false, sent);
        return "invalid";
      }
      // Offline, rate limited or the API is down: keep the session and try
      // again later instead of signing the user out.
      this.schedule(RETRY_DELAY_MS);
      return "transient";
    }
  }

  async bootstrap(): Promise<void> {
    try {
      const legacy = localStorage.getItem(LEGACY_TOKEN_KEY);
      const hint = isExtension
        ? ((await refreshStorage.get())?.split(".")[0] ?? null)
        : localStorage.getItem(SESSION_HINT_KEY);

      if (hint) {
        const outcome = await this.refreshOutcome();
        if (outcome === "ok") {
          return;
        }
        if (outcome === "transient") {
          // Stay signed in on the last known session so cached data shows;
          // the scheduled retry or the next request picks the token up.
          this.sessionKey = hint;
          this.status = "signed-in";
          this.emit();
          return;
        }
      }
      if (legacy && (await this.upgradeLegacy(legacy))) {
        return;
      }

      this.status = "signed-out";
      this.emit();
    } finally {
      this.resolveReady();
    }
  }

  // Sessions from builds before refresh tokens: move them over once, or drop
  // them if they were signed with a retired key.
  private async upgradeLegacy(token: string): Promise<boolean> {
    try {
      const response = await authClient.post("/auth/session/upgrade", null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await this.applySession(response.data.data);
      return true;
    } catch (error) {
      localStorage.removeItem(LEGACY_TOKEN_KEY);
      if (statusOf(error) === 401) {
        this.notice = "We upgraded account security. Please sign in again.";
      }
      return false;
    }
  }

  async logout(): Promise<void> {
    const token = this.accessToken;
    const body = isExtension
      ? { refresh_token: (await refreshStorage.get()) ?? undefined }
      : {};

    await this.clear();
    try {
      await authClient.post("/auth/session/logout", body, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
    } catch {
      // The session dies by itself if the server cannot be reached.
    }
  }

  // `failedToken` is the stored token a refresh was refused for; storage is
  // only emptied if it still holds that one.
  async clear(broadcast = true, failedToken?: string | null) {
    const wasSignedIn = this.status === "signed-in";
    const sessionKey = this.sessionKey;
    this.accessToken = null;
    this.expiresAt = 0;
    this.sessionKey = null;
    this.status = "signed-out";
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem(SESSION_HINT_KEY);
    if (
      isExtension &&
      (failedToken === undefined ||
        (await refreshStorage.get()) === failedToken)
    ) {
      await refreshStorage.clear();
    }
    this.emit();
    if (broadcast && wasSignedIn) {
      this.channel?.postMessage({ type: "signed-out", sessionKey });
    }
  }

  private schedule(delayMs: number) {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(
      () => {
        this.timer = null;
        if (this.status === "signed-in") {
          void this.refresh();
        }
      },
      Math.max(5_000, delayMs),
    );
  }

  private onBroadcast(message: { type?: string; sessionKey?: string }) {
    if (
      message?.type === "signed-out" &&
      this.status === "signed-in" &&
      (!message.sessionKey || message.sessionKey === this.sessionKey)
    ) {
      void this.clear(false);
    }
    if (
      message?.type === "signed-in" &&
      message.sessionKey !== this.sessionKey
    ) {
      // The other tab's cookie or stored token is shared, so pick it up.
      void this.refresh();
    }
  }

  private emit() {
    const state = this.getState();
    this.listeners.forEach(listener => listener(state));
  }
}

export const tokenManager = new TokenManager();
